import { fetchData, apiURL } from './api_helper';

// AI lesson generator — wraps the nexus-api /api/ai-lessons/* endpoints (spec §5.2).
// Mirrors the assignments.js convention: each call resolves result.data.
const base = `${apiURL}/api/ai-lessons`;

const getLimits = () => new Promise((resolve, reject) => {
  fetchData(`${base}/limits`, 'GET')
    .then(result => resolve(result.data)).catch(reject);
});

// Topic / goal / conversation-partner vocabularies for the speaking wizard, served from
// the live CORE catalogue (cached server-side, with a snapshot fallback). `targetLanguage`
// narrows the character roster to the personas CORE actually has for that language —
// Hungarian has two, so the old fixed list of eleven offered nine that CORE would reject.
// Topic and goal names come back localized to the request's Accept-Language.
// Resolves { topics, goals, characters, defaultCharacter, sources, ... }.
const getCatalogue = targetLanguage => new Promise((resolve, reject) => {
  // Query goes in the URL: fetchData turns a GET body into axiosConfig.data.
  fetchData(`${base}/catalogue?targetLanguage=${encodeURIComponent(targetLanguage || '')}`, 'GET')
    .then(result => resolve(result.data)).catch(reject);
});

const extract = body => new Promise((resolve, reject) => {
  fetchData(`${base}/extract`, 'POST', body)
    .then(result => resolve(result.data)).catch(reject);
});

const generate = body => new Promise((resolve, reject) => {
  fetchData(`${base}/generate`, 'POST', body)
    .then(result => resolve(result.data)).catch(reject);
});

// Crawl a web article URL server-side; resolves { text, title, sourceLanguage, truncated, meta }.
// `body` is { url, lessonType?, targetLanguage?, level?, sourceLanguage? }: when the extract
// params are present the backend also runs extract and returns `meta` (one round-trip).
const crawl = body => new Promise((resolve, reject) => {
  fetchData(`${base}/crawl`, 'POST', body)
    .then(result => resolve(result.data)).catch(reject);
});

// Preprocess a manually pasted source (clean noise, fix formatting, expand a thin
// source); resolves { text, action }. Used before extract on the manual-paste path.
const cleanSourceText = body => new Promise((resolve, reject) => {
  fetchData(`${base}/clean`, 'POST', body)
    .then(result => resolve(result.data)).catch(reject);
});

const getJob = jobId => new Promise((resolve, reject) => {
  fetchData(`${base}/jobs/${jobId}`, 'GET')
    .then(result => resolve(result.data)).catch(reject);
});

const cancelJob = jobId => new Promise((resolve, reject) => {
  fetchData(`${base}/jobs/${jobId}/cancel`, 'POST', {})
    .then(result => resolve(result.data)).catch(reject);
});

const get = id => new Promise((resolve, reject) => {
  fetchData(`${base}/${id}`, 'GET')
    .then(result => resolve(result.data)).catch(reject);
});

// Bulk-sync the wizard's working snapshot before save/publish/pdf.
const updateSnapshot = (id, content, title) => new Promise((resolve, reject) => {
  fetchData(`${base}/${id}/content`, 'PATCH', { content, title })
    .then(result => resolve(result.data)).catch(reject);
});

// AI ops are stateless: resolve { task } / { replies } the wizard merges locally.
// `tasks` is the current working snapshot (sibling context + count guard).
// `readingText` is the passage as currently edited above the tasks, so a regenerate
// reflects the teacher's edits instead of the original source.
const regenerateTask = (id, taskId, taskType, tasks, readingText) => new Promise((resolve, reject) => {
  fetchData(`${base}/${id}/tasks/${taskId}/regenerate`, 'POST', { taskType, tasks, readingText })
    .then(result => resolve(result.data)).catch(reject);
});

const editTask = (id, taskId, task) => new Promise((resolve, reject) => {
  fetchData(`${base}/${id}/tasks/${taskId}`, 'PATCH', { task })
    .then(result => resolve(result.data)).catch(reject);
});

const deleteTask = (id, taskId) => new Promise((resolve, reject) => {
  fetchData(`${base}/${id}/tasks/${taskId}`, 'DELETE')
    .then(result => resolve(result.data)).catch(reject);
});

const editConversation = (id, conversation) => new Promise((resolve, reject) => {
  fetchData(`${base}/${id}/conversation`, 'PATCH', { conversation })
    .then(result => resolve(result.data)).catch(reject);
});

const updateMeta = (id, meta) => new Promise((resolve, reject) => {
  fetchData(`${base}/${id}`, 'PATCH', meta)
    .then(result => resolve(result.data)).catch(reject);
});

const save = id => new Promise((resolve, reject) => {
  fetchData(`${base}/${id}/save`, 'POST', {})
    .then(result => resolve(result.data)).catch(reject);
});

// Speaking only. Materialises the lesson in CORE while the teacher is still on the
// Review step, so the preview can open the real thing. Unlike publish it does NOT lock
// the content, and creates no proxy lesson / favourite / assignment.
// Resolves { coreLessonId, corePushMocked }.
const previewSync = id => new Promise((resolve, reject) => {
  fetchData(`${base}/${id}/preview-sync`, 'POST', {})
    .then(result => resolve(result.data)).catch(reject);
});

// Throw away an unpublished draft: retracts the CORE lesson (if the preview created
// one) and soft-deletes the log. 409 on a published lesson.
const discard = id => new Promise((resolve, reject) => {
  fetchData(`${base}/${id}`, 'DELETE')
    .then(result => resolve(result.data)).catch(reject);
});

const publish = (id, body) => new Promise((resolve, reject) => {
  fetchData(`${base}/${id}/publish`, 'POST', body)
    .then(result => resolve(result.data)).catch(reject);
});

// Returns the raw arraybuffer response (api_helper switches to octet-stream when
// the body carries generatePdf). The caller turns it into a Blob download.
const exportPdf = (id, variant) => new Promise((resolve, reject) => {
  fetchData(`${base}/${id}/pdf`, 'POST', { generatePdf: true, variant })
    .then(result => resolve(result)).catch(reject);
});

export default {
  getLimits,
  getCatalogue,
  extract,
  generate,
  crawl,
  cleanSourceText,
  getJob,
  cancelJob,
  get,
  updateSnapshot,
  regenerateTask,
  editTask,
  deleteTask,
  editConversation,
  updateMeta,
  save,
  previewSync,
  discard,
  publish,
  exportPdf
};



// WEBPACK FOOTER //
// ./src/api/aiLessons.js